/**
 * Environment-aware logging utility
 *
 * Provides consistent logging across the application with automatic
 * environment detection. Debug logs are only shown in development mode.
 *
 * @example
 * ```typescript
 * import { logger } from '@/utils/logger';
 *
 * // Debug logs (development only)
 * logger.debug('Processing data', { data });
 *
 * // Info logs (always shown)
 * logger.info('User logged in', { userId });
 *
 * // Warning logs (always shown)
 * logger.warn('Deprecated API used', { endpoint });
 *
 * // Error logs (always shown)
 * logger.error('Failed to fetch data', error);
 * ```
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  /** Include timestamp in log output */
  timestamp?: boolean;
  /** Include log level prefix */
  prefix?: boolean;
}

const defaultOptions: LoggerOptions = {
  timestamp: false,
  prefix: true,
};

function formatMessage(level: LogLevel, message: string, options: LoggerOptions): string {
  const parts: string[] = [];

  if (options.timestamp) {
    parts.push(`[${new Date().toISOString()}]`);
  }

  if (options.prefix) {
    parts.push(`[${level.toUpperCase()}]`);
  }

  parts.push(message);

  return parts.join(' ');
}

/**
 * Logger instance with environment-aware logging methods
 */
export const logger = {
  /**
   * Debug log - only shown in development mode
   * Use for detailed debugging information that shouldn't appear in production
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.log(formatMessage('debug', message, defaultOptions), ...args);
    }
  },

  /**
   * Info log - shown in all environments
   * Use for general informational messages
   */
  info: (message: string, ...args: unknown[]): void => {
    console.info(formatMessage('info', message, defaultOptions), ...args);
  },

  /**
   * Warning log - shown in all environments
   * Use for potentially problematic situations that don't prevent operation
   */
  warn: (message: string, ...args: unknown[]): void => {
    console.warn(formatMessage('warn', message, defaultOptions), ...args);
  },

  /**
   * Error log - shown in all environments
   * Use for errors that affect application functionality
   */
  error: (message: string, ...args: unknown[]): void => {
    console.error(formatMessage('error', message, defaultOptions), ...args);
  },

  /**
   * Group related logs together (development only)
   * Useful for organizing related debug output
   */
  group: (label: string): void => {
    if (isDev) {
      console.group(formatMessage('debug', label, defaultOptions));
    }
  },

  /**
   * End a log group (development only)
   */
  groupEnd: (): void => {
    if (isDev) {
      console.groupEnd();
    }
  },

  /**
   * Log a table of data (development only)
   * Useful for debugging arrays and objects
   */
  table: (data: unknown): void => {
    if (isDev) {
      console.table(data);
    }
  },

  /**
   * Time a block of code (development only)
   * Returns a function to call when the operation completes
   *
   * @example
   * ```typescript
   * const end = logger.time('API call');
   * await fetchData();
   * end(); // Logs: "[DEBUG] API call: 123ms"
   * ```
   */
  time: (label: string): (() => void) => {
    if (isDev) {
      const start = performance.now();
      return () => {
        const duration = performance.now() - start;
        console.log(formatMessage('debug', `${label}: ${duration.toFixed(2)}ms`, defaultOptions));
      };
    }
    return () => {}; // No-op in production
  },
};

/**
 * Create a namespaced logger for a specific module
 *
 * @example
 * ```typescript
 * const log = createLogger('AuthService');
 * log.debug('Validating token'); // "[DEBUG] [AuthService] Validating token"
 * ```
 */
export function createLogger(namespace: string) {
  const prefix = `[${namespace}]`;

  return {
    debug: (message: string, ...args: unknown[]) => logger.debug(`${prefix} ${message}`, ...args),
    info: (message: string, ...args: unknown[]) => logger.info(`${prefix} ${message}`, ...args),
    warn: (message: string, ...args: unknown[]) => logger.warn(`${prefix} ${message}`, ...args),
    error: (message: string, ...args: unknown[]) => logger.error(`${prefix} ${message}`, ...args),
    group: (label: string) => logger.group(`${prefix} ${label}`),
    groupEnd: () => logger.groupEnd(),
    table: (data: unknown) => logger.table(data),
    time: (label: string) => logger.time(`${prefix} ${label}`),
  };
}

export default logger;
